import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  Download,
  Plus,
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  SortAsc,
  SortDesc,
  Calendar,
  MapPin,
  Users,
  Sprout,
  Building2,
  Truck,
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  BarChart3,
  PieChart,
  FileText,
  Mail,
  Phone,
  Globe,
  Settings,
  RefreshCw
} from 'lucide-react';

const AdvancedManagement = ({ 
  entityType = 'all', // 'producers', 'crops', 'centers', 'routes', 'all'
  userRole = 'admin' 
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    commune: 'all',
    dateRange: 'all',
    type: entityType === 'all' ? 'all' : entityType
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'grid', 'map'
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Mock data generators
  const generateMockProducers = () => [
    {
      id: 1,
      name: 'Juan Pérez',
      email: 'juan.perez@email.com',
      phone: '+58 412 123 4567',
      document: 'V-12345678',
      commune: 'Comuna 1',
      totalArea: 15.5,
      crops: ['Maíz', 'Tomate'],
      status: 'active',
      lastActivity: '2024-12-19',
      registrationDate: '2024-01-15',
      rating: 4.8,
      coordinates: { lat: 10.4806, lng: -66.9036 }
    },
    {
      id: 2,
      name: 'María García',
      email: 'maria.garcia@email.com',
      phone: '+58 414 987 6543',
      document: 'V-87654321',
      commune: 'Comuna 2',
      totalArea: 22.3,
      crops: ['Papa', 'Cebolla'],
      status: 'active',
      lastActivity: '2024-12-18',
      registrationDate: '2024-02-20',
      rating: 4.6,
      coordinates: { lat: 10.4900, lng: -66.8900 }
    },
    {
      id: 3,
      name: 'Carlos López',
      email: 'carlos.lopez@email.com',
      phone: '+58 416 555 1234',
      document: 'V-11223344',
      commune: 'Comuna 3',
      totalArea: 8.7,
      crops: ['Lechuga', 'Pimiento'],
      status: 'inactive',
      lastActivity: '2024-12-10',
      registrationDate: '2024-03-10',
      rating: 4.2,
      coordinates: { lat: 10.4700, lng: -66.9200 }
    }
  ];

  const generateMockCrops = () => [
    {
      id: 1,
      name: 'Cultivo de Maíz - Finca El Progreso',
      producer: 'Juan Pérez',
      type: 'Maíz',
      area: 8.5,
      commune: 'Comuna 1',
      plantingDate: '2024-11-15',
      expectedHarvest: '2024-12-20',
      status: 'growing',
      yield: 85,
      lastUpdate: '2024-12-19',
      coordinates: { lat: 10.4810, lng: -66.9040 }
    },
    {
      id: 2,
      name: 'Cultivo de Tomate - Huerto Familiar',
      producer: 'Juan Pérez',
      type: 'Tomate',
      area: 2.3,
      commune: 'Comuna 1',
      plantingDate: '2024-12-01',
      expectedHarvest: '2024-12-25',
      status: 'growing',
      yield: 92,
      lastUpdate: '2024-12-18',
      coordinates: { lat: 10.4820, lng: -66.9050 }
    },
    {
      id: 3,
      name: 'Cultivo de Papa - Campo Grande',
      producer: 'María García',
      type: 'Papa',
      area: 12.8,
      commune: 'Comuna 2',
      plantingDate: '2024-10-20',
      expectedHarvest: '2024-12-15',
      status: 'ready',
      yield: 78,
      lastUpdate: '2024-12-17',
      coordinates: { lat: 10.4910, lng: -66.8910 }
    }
  ];

  const generateMockCenters = () => [
    {
      id: 1,
      name: 'Centro de Acopio Central',
      type: 'acopio',
      commune: 'Comuna 1',
      capacity: 500,
      currentStock: 320,
      contactPerson: 'Ana Rodríguez',
      phone: '+58 424 111 2222',
      email: 'centro.central@sisciac.com',
      status: 'active',
      lastUpdate: '2024-12-19',
      coordinates: { lat: 10.4830, lng: -66.9060 }
    },
    {
      id: 2,
      name: 'Centro de Distribución Norte',
      type: 'distribucion',
      commune: 'Comuna 2',
      capacity: 300,
      currentStock: 180,
      contactPerson: 'Roberto Silva',
      phone: '+58 426 333 4444',
      email: 'distribucion.norte@sisciac.com',
      status: 'active',
      lastUpdate: '2024-12-18',
      coordinates: { lat: 10.4920, lng: -66.8920 }
    }
  ];

  const generateMockRoutes = () => [
    {
      id: 1,
      name: 'Ruta Principal - Centro a Norte',
      type: 'logistics',
      startPoint: 'Centro de Acopio Central',
      endPoint: 'Centro de Distribución Norte',
      distance: 25.5,
      estimatedTime: 45,
      status: 'active',
      lastUsed: '2024-12-19',
      efficiency: 88,
      coordinates: [
        { lat: 10.4830, lng: -66.9060 },
        { lat: 10.4920, lng: -66.8920 }
      ]
    },
    {
      id: 2,
      name: 'Ruta Alternativa - Norte a Sur',
      type: 'logistics',
      startPoint: 'Centro de Distribución Norte',
      endPoint: 'Centro de Procesamiento Sur',
      distance: 18.3,
      estimatedTime: 35,
      status: 'maintenance',
      lastUsed: '2024-12-15',
      efficiency: 75,
      coordinates: [
        { lat: 10.4920, lng: -66.8920 },
        { lat: 10.4750, lng: -66.9150 }
      ]
    }
  ];

  useEffect(() => {
    loadData();
  }, [entityType, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Determinar qué datos cargar según el tipo de entidad
      let apiEndpoint = '';
      let mockData = [];
      
      switch (entityType) {
        case 'producers':
          apiEndpoint = '/api/producers';
          mockData = generateMockProducers();
          break;
        case 'crops':
          apiEndpoint = '/api/crops';
          mockData = generateMockCrops();
          break;
        case 'centers':
          apiEndpoint = '/api/collection-centers';
          mockData = generateMockCenters();
          break;
        case 'routes':
          apiEndpoint = '/api/logistics-routes';
          mockData = generateMockRoutes();
          break;
        case 'all':
        default:
          // Combinar todos los datos
          mockData = [
            ...generateMockProducers().map(item => ({ ...item, entityType: 'producer' })),
            ...generateMockCrops().map(item => ({ ...item, entityType: 'crop' })),
            ...generateMockCenters().map(item => ({ ...item, entityType: 'center' })),
            ...generateMockRoutes().map(item => ({ ...item, entityType: 'route' }))
          ];
          break;
      }

      if (apiEndpoint && entityType !== 'all') {
        try {
          const response = await fetch(apiEndpoint);
          const apiData = await response.json();
          const processedData = apiData.data || apiData || [];
          setData(processedData);
        } catch (error) {
          console.error(`Error loading ${entityType}:`, error);
          setData(mockData);
        }
      } else {
        setData(mockData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar y ordenar datos
  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(item => {
      // Filtro de búsqueda
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const searchableFields = [
          item.name,
          item.email,
          item.phone,
          item.document,
          item.commune,
          item.type,
          item.producer
        ].filter(Boolean);
        
        if (!searchableFields.some(field => 
          field.toString().toLowerCase().includes(searchLower)
        )) {
          return false;
        }
      }

      // Filtros específicos
      if (filters.status !== 'all' && item.status !== filters.status) {
        return false;
      }
      
      if (filters.commune !== 'all' && item.commune !== filters.commune) {
        return false;
      }
      
      if (filters.type !== 'all' && item.entityType !== filters.type) {
        return false;
      }

      return true;
    });

    // Ordenamiento
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [data, searchTerm, filters, sortConfig]);

  // Paginación
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems.length === paginatedData.length 
        ? [] 
        : paginatedData.map(item => item.id)
    );
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action} on items:`, selectedItems);
    // Implementar acciones en lote
    setSelectedItems([]);
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      producer: Users,
      crop: Sprout,
      center: Building2,
      route: Truck
    };
    return icons[entityType] || FileText;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-green-600 bg-green-100',
      inactive: 'text-gray-600 bg-gray-100',
      growing: 'text-blue-600 bg-blue-100',
      ready: 'text-green-600 bg-green-100',
      maintenance: 'text-yellow-600 bg-yellow-100',
      pending: 'text-orange-600 bg-orange-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getUniqueCommunes = () => {
    return [...new Set(data.map(item => item.commune).filter(Boolean))];
  };

  const getUniqueStatuses = () => {
    return [...new Set(data.map(item => item.status).filter(Boolean))];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión Avanzada</h1>
          <p className="text-gray-600">
            {entityType === 'all' ? 'Todas las entidades' : 
             entityType === 'producers' ? 'Productores' :
             entityType === 'crops' ? 'Cultivos' :
             entityType === 'centers' ? 'Centros de Acopio' :
             entityType === 'routes' ? 'Rutas Logísticas' : 'Gestión de datos'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadData}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2 inline" />
            Actualizar
          </button>
          <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
            <Plus className="h-4 w-4 mr-2 inline" />
            Agregar
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, email, teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </button>
            
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="table">Tabla</option>
              <option value="grid">Cuadrícula</option>
              <option value="map">Mapa</option>
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value={5}>5 por página</option>
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
            </select>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-6 pt-6 border-t border-gray-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">Todos</option>
                  {getUniqueStatuses().map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Comuna</label>
                <select
                  value={filters.commune}
                  onChange={(e) => setFilters(prev => ({ ...prev, commune: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">Todas</option>
                  {getUniqueCommunes().map(commune => (
                    <option key={commune} value={commune}>{commune}</option>
                  ))}
                </select>
              </div>

              {entityType === 'all' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="all">Todos</option>
                    <option value="producer">Productores</option>
                    <option value="crop">Cultivos</option>
                    <option value="center">Centros</option>
                    <option value="route">Rutas</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rango de Fecha</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">Todas las fechas</option>
                  <option value="today">Hoy</option>
                  <option value="week">Esta semana</option>
                  <option value="month">Este mes</option>
                  <option value="quarter">Este trimestre</option>
                  <option value="year">Este año</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-blue-800 font-medium">
              {selectedItems.length} elemento{selectedItems.length !== 1 ? 's' : ''} seleccionado{selectedItems.length !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkAction('export')}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Exportar
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Eliminar
              </button>
              <button
                onClick={() => setSelectedItems([])}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cancelar
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Results Summary */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              Mostrando {paginatedData.length} de {filteredAndSortedData.length} resultado{filteredAndSortedData.length !== 1 ? 's' : ''}
            </span>
            {searchTerm && (
              <span className="text-sm text-blue-600">
                Filtrado por: "{searchTerm}"
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
              <Download className="h-4 w-4 mr-1 inline" />
              Exportar
            </button>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </th>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <span>Nombre</span>
                    {sortConfig.key === 'name' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                    )}
                  </button>
                </th>
                {entityType === 'all' && (
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('entityType')}
                      className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                    >
                      <span>Tipo</span>
                      {sortConfig.key === 'entityType' && (
                        sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                      )}
                    </button>
                  </th>
                )}
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('commune')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <span>Comuna</span>
                    {sortConfig.key === 'commune' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <span>Estado</span>
                    {sortConfig.key === 'status' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('lastActivity')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <span>Última Actividad</span>
                    {sortConfig.key === 'lastActivity' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.map((item) => {
                const EntityIcon = getEntityIcon(item.entityType);
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <EntityIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          {item.email && (
                            <div className="text-sm text-gray-500">{item.email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    {entityType === 'all' && (
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.entityType === 'producer' ? 'Productor' :
                           item.entityType === 'crop' ? 'Cultivo' :
                           item.entityType === 'center' ? 'Centro' :
                           item.entityType === 'route' ? 'Ruta' : item.entityType}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">{item.commune}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status === 'active' ? 'Activo' :
                         item.status === 'inactive' ? 'Inactivo' :
                         item.status === 'growing' ? 'Creciendo' :
                         item.status === 'ready' ? 'Listo' :
                         item.status === 'maintenance' ? 'Mantenimiento' :
                         item.status === 'pending' ? 'Pendiente' : item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">
                          {new Date(item.lastActivity || item.lastUpdate).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="text-blue-600 hover:text-blue-900">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="text-green-600 hover:text-green-900">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando página <span className="font-medium">{currentPage}</span> de{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-green-50 border-green-500 text-green-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedManagement;
